import { Box, IconButton, List, ListItem, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { NewRequestModal } from "../requests/newRequestModal.container";
import { AddStep } from "../../components/AddStep";
import { NewStepModal } from "./newStepModal.container";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { EditOutlined, OpenWith } from "@mui/icons-material";
import { GRAY_60, GRAY_INPUT } from "../../themes/colors";
import {
  DateFormat,
  getFormattedDate,
  getTotalRestrictions,
} from "../../util/utils";
import { useParams } from "react-router-dom";
import { useRequestsActions } from "../../contexts/requests/UseRequestsActions";
import { useStepsContext } from "../../contexts/steps/StepsContext";
import Step from "../../core/models/Step";
import { useStepsActions } from "../../contexts/steps/UseStepsActions";
import { useIntl } from "react-intl";
import { InfoBox } from "../../components/InfoBox";
import { useAuthContext } from "../../contexts/authentication/AuthenticationContext";
import { Role } from "../../core/models/User";
import { useRequestsContext } from "../../contexts/requests/RequestsContext";

const Steps = () => {
  const intl = useIntl();
  const params = useParams<{ id: string }>();
  const { userInfo } = useAuthContext();
  const isBusinessUser = userInfo?.permissionRole === Role.Business;
  const [openNewRequestModal, setOpenNewRequestModal] =
    useState<boolean>(false);
  const [selectedStepId, setSelectedStepId] = useState<string>();
  const [openNewStepModal, setOpenNewStepModal] = useState<boolean>(false);

  const { stepsResponseData } = useStepsContext();
  const [steps, setSteps] = useState<Step[]>(stepsResponseData?.data || []);
  const { saveRequestDetailsTrigger, setSaveRequestDetailsTrigger } =
    useRequestsContext();
  const {
    dispatchGetSteps,
    dispatchCleanStepsContext,
    dispatchUpdateStepsBatch,
  } = useStepsActions();

  const { dispatchGetRequest } = useRequestsActions();

  //#region effects
  useEffect(() => {
    if (params.id) dispatchGetRequest(params.id);
    dispatchGetSteps({
      filter: {
        integration_configuration: params.id,
      },
      page: 1,
      size: 1000,
    });
    return () => {
      dispatchCleanStepsContext();
    };
  }, []);

  useEffect(() => {
    if (stepsResponseData) {
      setSteps(stepsResponseData.data);
    }
  }, [stepsResponseData]);

  useEffect(() => {
    //clicked status
    if (saveRequestDetailsTrigger === -1) {
      handleOnSave();
    }
  }, [saveRequestDetailsTrigger]); // Runs when Save button is clicked
  //#endregion

  //#region Page actions
  const handleOnSave = async () => {
    try {
      await dispatchUpdateStepsBatch(steps); // Disable save button after saving
      setSaveRequestDetailsTrigger(0);
    } catch (error) {
      console.error("Failed to update step order", error);
    }
  };
  //#endregion

  //#region steps actions
  const onClickEditStep = (step: Step) => {
    setSelectedStepId(step.id);
    setOpenNewStepModal(true);
  };

  const toggleOpenNewStepModal = () => {
    setOpenNewStepModal(!openNewStepModal);
  };

  const onAddStep = () => {
    setSelectedStepId("");
    toggleOpenNewStepModal();
  };

  const handleOnDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const reorderedItems = [...steps];
    const [movedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, movedItem);

    setSteps(reorderedItems);
    setSaveRequestDetailsTrigger(saveRequestDetailsTrigger + 1); // Enable save button when order changes
  };
  //#endregion

  //#region request action
  const toggleOpenNewRequestModal = () => {
    setOpenNewRequestModal(!openNewRequestModal);
  };
  //#endregion
  return (
    <>
      <Box id="pageWrapper" className="w-full flex flex-col flex-1">
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId="droppable-list">
            {(provided) => (
              <List {...provided.droppableProps} ref={provided.innerRef}>
                {steps.map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={item.id}
                    index={index}
                    isDragDisabled={isBusinessUser}
                  >
                    {(provided) => (
                      <ListItem
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="p-6 mb-5 rounded-lg"
                        sx={{ backgroundColor: GRAY_INPUT }}
                        data-testid={"step." + item.id}
                      >
                        <Box className="w-full">
                          <Box className="w-full flex flex-row justify-between items-center">
                            <Box className="flex space-x-[0.625rem] items-center">
                              {!isBusinessUser ? <OpenWith /> : null}
                              <Typography
                                flexDirection={"row"}
                                flex={1}
                                display={"flex"}
                                variant="h2"
                              >
                                {`Step ${index + 1}`}
                                <Typography
                                  variant="h2"
                                  fontWeight={400}
                                >{` | ${item.title}`}</Typography>
                              </Typography>
                              {!isBusinessUser ? (
                                <IconButton
                                  data-testid={`${item.id}.editStep`}
                                  aria-label={`edit step ${item.id}`}
                                  onClick={() => onClickEditStep(item)}
                                >
                                  <EditOutlined color="primary" />
                                </IconButton>
                              ) : null}
                            </Box>
                            <Box className="flex space-x-[0.625rem]">
                              <Typography variant="body2">
                                {
                                  item.presentation_configuration
                                    ?.requestedAttributes?.length
                                }{" "}
                                {intl.formatMessage({
                                  id: "step.new.modal.attributes",
                                })}{" "}
                                |{" "}
                                {getTotalRestrictions(
                                  item?.presentation_configuration
                                    ?.requestedAttributes
                                )}{" "}
                                {intl.formatMessage({
                                  id: "step.new.modal.credentialDefinitions",
                                })}
                              </Typography>
                            </Box>
                          </Box>
                          {item.is_mandatory || item.is_multiple ? (
                            <InfoBox
                              text={[
                                item.is_mandatory &&
                                  intl.formatMessage({
                                    id: "step.list.attribute.mandatory",
                                  }),
                                item.is_multiple &&
                                  intl.formatMessage({
                                    id: "step.list.credential.multiple",
                                  }),
                              ]
                                .filter(Boolean) // Remove falsy values (undefined, false)
                                .join(" ")} // Use a separator if needed
                            />
                          ) : null}
                          <Box className="rounded-md p-5 bg-white mt-4">
                            <Typography variant="h6">{item.title}</Typography>
                            <Typography variant="body2" color={GRAY_60}>
                              {getFormattedDate(
                                item.modified,
                                DateFormat.DayOfMonth
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      </ListItem>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
        <Box className="h-full">
          <AddStep onAddStep={onAddStep} />
        </Box>
      </Box>
      {openNewRequestModal && (
        <NewRequestModal
          open={openNewRequestModal}
          id={params.id}
          onClose={() => toggleOpenNewRequestModal()}
        />
      )}
      {openNewStepModal && (
        <NewStepModal
          open={openNewStepModal}
          requestId={params.id}
          id={selectedStepId}
          onClose={() => toggleOpenNewStepModal()}
        />
      )}
    </>
  );
};

export { Steps };
